/**
 * APX WorkCenter — NotifyClientModal
 * Allows Controllers to send quick status updates or document requests
 * directly to a client's email from within the dashboard.
 *
 * Features:
 * - Client selector (searchable dropdown from live DB or free-text email)
 * - Message type: Status Update | Document Request | P&L Ready | Month-End Close | Custom
 * - Pre-populated subject and body templates per message type
 * - Sender signature auto-filled from logged-in user
 */

import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// ─── Message type templates ────────────────────────────────────────────────────
const MESSAGE_TYPES = [
  { value: "status_update", label: "Status Update", icon: "📋" },
  { value: "document_request", label: "Document Request", icon: "📄" },
  { value: "pl_ready", label: "P&L Ready", icon: "📊" },
  { value: "month_end_close", label: "Month-End Close", icon: "✅" },
  { value: "custom", label: "Custom", icon: "✉️" },
];

function getTemplate(type: string, clientName: string, senderName: string) {
  switch (type) {
    case "status_update":
      return {
        subject: `Account Update — ${clientName}`,
        body: `Hi ${clientName} team,\n\nI wanted to provide a quick update on your account. Please feel free to reach out if you have any questions.\n\nBest regards,\n${senderName}\nAPX Financial Operations`,
      };
    case "document_request":
      return {
        subject: `Document Request — ${clientName}`,
        body: `Hi ${clientName} team,\n\nTo keep your books current, we need the following documents from you:\n\n• [List documents here]\n\nPlease send them at your earliest convenience. Thank you!\n\nBest regards,\n${senderName}\nAPX Financial Operations`,
      };
    case "pl_ready":
      return {
        subject: `Your P&L Report is Ready — ${clientName}`,
        body: `Hi ${clientName} team,\n\nYour Profit & Loss report for the current period is ready for your review. Please log in to your portal or reply to this email to receive a copy.\n\nBest regards,\n${senderName}\nAPX Financial Operations`,
      };
    case "month_end_close":
      return {
        subject: `Month-End Close Complete — ${clientName}`,
        body: `Hi ${clientName} team,\n\nWe have completed the month-end close for your account. Your financial statements are now up to date. Please reach out if you have any questions or need a review call.\n\nBest regards,\n${senderName}\nAPX Financial Operations`,
      };
    default:
      return { subject: "", body: "" };
  }
}

interface Props {
  onClose: () => void;
  defaultClientName?: string;
  defaultClientEmail?: string;
}

export default function NotifyClientModal({
  onClose,
  defaultClientName = "",
  defaultClientEmail = "",
}: Props) {
  const { user } = useAuth();
  const [messageType, setMessageType] = useState("status_update");
  const [clientSearch, setClientSearch] = useState(defaultClientName);
  const [clientEmail, setClientEmail] = useState(defaultClientEmail);
  const [useManualEmail, setUseManualEmail] = useState(!!defaultClientEmail && !defaultClientName);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [subject, setSubject] = useState(() => getTemplate("status_update", defaultClientName || "Client", user?.name ?? "").subject);
  const [body, setBody] = useState(() => getTemplate("status_update", defaultClientName || "Client", user?.name ?? "").body);

  // Load live client list from DB
  const { data: allClients = [] } = trpc.clientStatus.listAll.useQuery();

  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return allClients.slice(0, 8);
    const q = clientSearch.toLowerCase();
    return allClients.filter((c) => c.clientName.toLowerCase().includes(q)).slice(0, 8);
  }, [allClients, clientSearch]);

  const send = trpc.clientNotifications.send.useMutation({
    onSuccess: () => {
      toast.success("Client notified", {
        description: `Message sent to ${clientSearch || clientEmail}`,
      });
      onClose();
    },
    onError: (err) => toast.error("Failed to send", { description: err.message }),
  });

  const handleTypeChange = (type: string) => {
    setMessageType(type);
    if (type !== "custom") {
      const tpl = getTemplate(type, clientSearch || "Client", user?.name ?? "");
      setSubject(tpl.subject);
      setBody(tpl.body);
    }
  };

  const handleClientSelect = (name: string, email?: string | null) => {
    setClientSearch(name);
    if (email) setClientEmail(email);
    setShowClientDropdown(false);
    // Refresh template with real client name
    if (messageType !== "custom") {
      const tpl = getTemplate(messageType, name, user?.name ?? "");
      setSubject(tpl.subject);
      setBody(tpl.body);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const destination = clientEmail.trim();
    const clientNameFinal = clientSearch.trim() || "Client";
    if (!destination || !clientNameFinal) return;
    if (!subject.trim() || !body.trim()) return;

    send.mutate({
      senderName: user?.name ?? "Controller",
      senderEmail: user?.email ?? "",
      clientName: clientNameFinal,
      clientEmail: destination,
      messageType,
      subject: subject.trim(),
      body: body.trim(),
    });
  };

  const canSend = clientEmail.trim() && (clientSearch.trim() || useManualEmail) && subject.trim() && body.trim();

  const inputStyle = {
    background: "oklch(0.93 0.01 220)",
    border: "1px solid oklch(0.75 0.02 220)",
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "oklch(0 0 0 / 65%)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: "oklch(0.96 0.01 220)", border: "1px solid oklch(0.82 0.02 220)" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: "1px solid oklch(0.85 0.02 220)" }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-sm"
              style={{ background: "oklch(0.92 0.005 240 / 15%)" }}
            >
              📨
            </div>
            <div>
              <h2
                className="text-sm font-bold text-[oklch(0.08_0.01_240)]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Notify Client
              </h2>
              <p className="text-sm text-[oklch(0.35_0.02_240)]">
                Send a quick update or request directly to a client
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-[oklch(0.35_0.02_240)] hover:text-[oklch(0.18_0.04_240)] transition-colors"
            style={{ background: "oklch(1 0 0 / 6%)" }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          {/* ── Message type ──────────────────────────────────────── */}
          <div>
            <label className="block text-sm font-semibold text-[oklch(0.08_0.01_240)] uppercase tracking-wider mb-2">
              Message Type
            </label>
            <div className="flex gap-1.5 flex-wrap">
              {MESSAGE_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => handleTypeChange(t.value)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-all duration-150"
                  style={{
                    background: messageType === t.value ? "oklch(0.25 0.06 240)" : "oklch(0.88 0.01 220)",
                    border: `1px solid ${messageType === t.value ? "oklch(0.35 0.06 240)" : "oklch(0.75 0.02 220)"}`,
                    color: messageType === t.value ? "oklch(0.95 0.005 240)" : "oklch(0.18 0.04 240)",
                  }}
                >
                  <span>{t.icon}</span>
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Client selector ───────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-[oklch(0.08_0.01_240)] uppercase tracking-wider">
                Client <span style={{ color: "oklch(0.08 0.01 240)" }}>*</span>
              </label>
              <button
                type="button"
                onClick={() => setUseManualEmail(!useManualEmail)}
                className="text-sm transition-colors"
                style={{ color: "oklch(0.08 0.01 240)" }}
              >
                {useManualEmail ? "← Select from portfolio" : "Enter email manually →"}
              </button>
            </div>

            {!useManualEmail ? (
              <div className="relative">
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(true); }}
                  onFocus={() => setShowClientDropdown(true)}
                  onBlur={() => setTimeout(() => setShowClientDropdown(false), 150)}
                  placeholder="Search client name…"
                  autoComplete="off"
                  className="w-full px-3 py-2.5 rounded-lg text-sm text-[oklch(0.18_0.04_240)] placeholder:text-[oklch(0.35_0.02_240)] outline-none"
                  style={inputStyle}
                />
                {showClientDropdown && filteredClients.length > 0 && (
                  <div
                    className="absolute top-full left-0 right-0 mt-1 rounded-xl shadow-2xl z-10 overflow-hidden"
                    style={{ background: "oklch(0.96 0.01 220)", border: "1px solid oklch(0.82 0.02 220)" }}
                  >
                    {filteredClients.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onMouseDown={() => handleClientSelect(c.clientName, (c as any).clientEmail)}
                        className="w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors"
                        style={{ borderBottom: "1px solid oklch(0.88 0.01 220)" }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "oklch(1 0 0 / 5%)"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                      >
                        <div className="flex items-center gap-2.5">
                          <div
                            className="w-6 h-6 rounded-md flex items-center justify-center text-sm font-bold shrink-0"
                            style={{ background: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.08 0.01 240)" }}
                          >
                            {c.clientName.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm text-[oklch(0.18_0.04_240)]">{c.clientName}</span>
                        </div>
                        <span
                          className="text-sm px-2 py-0.5 rounded-full"
                          style={{
                            background: c.status === "complete"
                              ? "oklch(0.92 0.005 240 / 15%)"
                              : c.status === "overdue"
                              ? "oklch(0.92 0.005 240 / 15%)"
                              : "oklch(0.55 0.02 240 / 15%)",
                            color: c.status === "complete"
                              ? "oklch(0.55 0.01 240)"
                              : c.status === "overdue"
                              ? "oklch(0.55 0.01 240)"
                              : "oklch(0.55 0.02 240)",
                          }}
                        >
                          {c.bookkeeperBankFeed}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="client@example.com"
                autoComplete="off"
                required
                className="w-full px-3 py-2.5 rounded-lg text-sm text-[oklch(0.18_0.04_240)] placeholder:text-[oklch(0.35_0.02_240)] outline-none"
                style={inputStyle}
              />
            )}
          </div>

          {/* ── Client email ─────────────────────────────────────── */}
          <div>
            <label className="block text-sm font-semibold text-[oklch(0.08_0.01_240)] uppercase tracking-wider mb-2">
              Client Email <span style={{ color: "oklch(0.08 0.01 240)" }}>*</span>
              {!useManualEmail && clientEmail && (
                <span className="ml-2 text-sm font-normal text-[oklch(0.08_0.01_240)]">(auto-filled from database)</span>
              )}
            </label>
            <input
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder={useManualEmail ? "client@example.com" : "Enter or auto-fill from client selection…"}
              autoComplete="off"
              required
              className="w-full px-3 py-2.5 rounded-lg text-sm text-[oklch(0.18_0.04_240)] placeholder:text-[oklch(0.35_0.02_240)] outline-none"
              style={inputStyle}
            />
          </div>

          {/* ── Subject ───────────────────────────────────────────── */}
          <div>
            <label className="block text-sm font-semibold text-[oklch(0.08_0.01_240)] uppercase tracking-wider mb-2">
              Subject <span style={{ color: "oklch(0.08 0.01 240)" }}>*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Your P&L report is ready"
              required
              autoComplete="off"
              className="w-full px-3 py-2.5 rounded-lg text-sm text-[oklch(0.18_0.04_240)] placeholder:text-[oklch(0.35_0.02_240)] outline-none"
              style={inputStyle}
            />
          </div>

          {/* ── Message body ──────────────────────────────────────── */}
          <div>
            <label className="block text-sm font-semibold text-[oklch(0.08_0.01_240)] uppercase tracking-wider mb-2">
              Message <span style={{ color: "oklch(0.08 0.01 240)" }}>*</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              rows={5}
              className="w-full px-3 py-2.5 rounded-lg text-sm text-[oklch(0.18_0.04_240)] placeholder:text-[oklch(0.35_0.02_240)] outline-none resize-none"
              style={inputStyle}
            />
          </div>

          {/* ── Sender badge ──────────────────────────────────────── */}
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
            style={{ background: "oklch(0.93 0.01 220)", border: "1px solid oklch(0.80 0.02 220)" }}
          >
            <svg className="w-3.5 h-3.5 text-[oklch(0.08_0.01_240)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-[oklch(0.35_0.02_240)]">Sending as</span>
            <span className="font-semibold text-[oklch(0.28_0.04_240)]">{user?.name}</span>
            <span className="text-[oklch(0.35_0.02_240)]">·</span>
            <span className="text-[oklch(0.35_0.02_240)]">APX Financial Operations</span>
          </div>

          {/* ── Actions ───────────────────────────────────────────── */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-medium text-[oklch(0.08_0.01_240)] transition-colors"
              style={{ background: "oklch(0.96 0.01 220)", border: "1px solid oklch(0.82 0.02 220)" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSend || send.isPending}
              className="flex-1 py-2.5 rounded-lg text-sm font-bold transition-all duration-150"
              style={{
                background: canSend
                  ? "linear-gradient(135deg, oklch(0.82 0.01 240), oklch(0.55 0.01 240))"
                  : "oklch(0.14 0.04 240)",
                color: canSend ? "oklch(0.15 0.04 240)" : "oklch(0.35 0.02 240)",
                opacity: send.isPending ? 0.7 : 1,
                cursor: canSend ? "pointer" : "not-allowed",
                border: canSend ? "none" : "1px solid oklch(0.75 0.02 220)",
              }}
            >
              {send.isPending ? "Sending…" : "Send to Client"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
